<template>
    <div class="edit-subscription">
        <transition name="dialogfade">
            <k-dialog v-if="showDialog" class="sm-dialog edit-subscription-dialog" :title="dialogTitle" :title-render="'smTitle'" @close="onCloseDialog">
                <template v-slot:smTitle>
                    <span class="sm-dialog-heading">
                        <span class="sm-dialog-icon" aria-hidden="true"><i class="fa-solid fa-sliders"></i></span>
                        <span class="sm-dialog-heading-text">
                            Manage subscription
                            <span class="sm-dialog-subtitle">{{ dialogSubtitle }}</span>
                        </span>
                    </span>
                </template>
                <div class="edit-container">
                    <section class="edit-section">
                        <h3 class="section-title">Status</h3>
                        <div class="status-options" role="radiogroup" aria-label="Subscription status">
                            <label v-for="option in statusOptions"
                                   :key="option.value"
                                   :class="['status-option', { active: editedSubscriptionStatus === option.value }]">
                                <input type="radio"
                                       class="k-radio k-radio-md"
                                       name="subscription-status"
                                       :value="option.value"
                                       :checked="editedSubscriptionStatus === option.value"
                                       @change="updateStatus(option.value)">
                                <span class="status-option-text">
                                    <span class="status-option-label">{{ option.label }}</span>
                                    <span class="status-option-help">{{ option.help }}</span>
                                </span>
                            </label>
                        </div>
                    </section>

                    <section v-if="editedSubscriptionStatus === 'Partially Unsubscribed'" class="edit-section loans-section">
                        <h3 class="section-title">Turned off for these loans</h3>
                        <form class="loan-input-row" @submit.prevent="onAddLoanNumber">
                            <k-input class="loan-number-input"
                                     v-model="unsubscribedLoanNumber"
                                     placeholder="Loan number"
                                     aria-label="Loan number"
                                     @input="resetErrors" />
                            <k-button type="submit" class="sm-btn sm-btn-primary add-loan-btn" :disabled="!unsubscribedLoanNumber">Add</k-button>
                        </form>
                        <p v-if="showDuplicateLoanError" class="field-error"><i class="fa-regular fa-circle-exclamation"></i> That loan is already on the list</p>
                        <p v-if="showInvalidLoanError" class="field-error"><i class="fa-regular fa-circle-exclamation"></i> Loan not found</p>

                        <div class="loan-list">
                            <div v-for="loan in unsubscribedLoans" :key="loan.loanId" class="loan-row">
                                <span class="borrower-name">{{ loan.borrowerFirst }} {{ loan.borrowerLast }}</span>
                                <span class="loan-number">{{ loan.loanNumber }}</span>
                                <button type="button"
                                        class="remove-loan"
                                        title="Remove loan"
                                        :aria-label="`Remove loan ${loan.loanNumber}`"
                                        @click="onRemoveLoan(loan)"><i class="fa-regular fa-xmark"></i></button>
                            </div>
                            <p v-if="!unsubscribedLoans || unsubscribedLoans.length === 0" class="empty-loans">Add at least one loan to save.</p>
                        </div>
                    </section>
                </div>
                <k-action-bar>
                    <k-button class="sm-btn" @click="onCancelClicked">Cancel</k-button>
                    <k-button class="sm-btn sm-btn-primary" @click="onSaveClicked" :disabled="saveChangesBtnDisabled">Save</k-button>
                </k-action-bar>

                <div class="component__loader-container" v-if="loading || loadingUpdate">
                    <k-loader size="large" type="infinite-spinner"></k-loader>
                </div>
            </k-dialog>
        </transition>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" :title-render="'smTitle'" class="sm-dialog sm-dialog-error" @close="toggleErrorDialog">
                <template v-slot:smTitle>
                    <span class="sm-dialog-heading">
                        <span class="sm-dialog-icon danger" aria-hidden="true"><i class="fa-solid fa-circle-exclamation"></i></span>
                        <span class="sm-dialog-heading-text">{{ errorTitle }}</span>
                    </span>
                </template>
                <p class="sm-dialog-msg">{{ errorMsg }}</p>
                <k-action-bar>
                    <k-button class="sm-btn sm-btn-primary" @click="toggleErrorDialog">OK</k-button>
                </k-action-bar>
            </k-dialog>
        </transition>

        <k-notification-group class="default__notification-container">
            <k-fade :appear="successNotification">
                <k-notification v-if="successNotification"
                                :type="{ style: 'success', icon: true }"
                                :closable="true"
                                @close="onCloseNotification('successNotification')">
                    <span>{{ notificationMsg }}</span>
                </k-notification>
            </k-fade>
        </k-notification-group>
    </div>
</template>

<script>
    import { Dialog, DialogActionsBar } from '@progress/kendo-vue-dialogs';
    import { Button } from "@progress/kendo-vue-buttons";
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Notification, NotificationGroup } from "@progress/kendo-vue-notification";
    import { Fade } from "@progress/kendo-vue-animation";
    import { Input } from '@progress/kendo-vue-inputs';

    import apiService from '../api/api.service.js';
    import auth from '../authConfig.js';

    import cloneDeep from 'lodash.clonedeep';
    import isEqual from 'lodash.isequal';

    import subscriptionHelpers from '../helpers/subscriptionHelpers.js';

    export default {
        props: ['allNotifications', 'azureId', 'currentSubscriptionStatus', 'loadingUpdate', 'notification', 'notificationSubscriptions', 
                'notificationType', 'showDialog', 'subscribedToAllPush', 'subscribedToAllPushItem', 'subscribedToAllSms',
                'subscribedToAllSmsItem', 'unsubscribedFromAllEmails', 'unsubscribedFromAllEmailsItem'],
        emits: ['dialogclosed', 'subscriptionsupdated'],
        name: 'editSubscriptionDialog',
        data() {
            return {
                editedSubscriptions: [],
                editedSubscriptionStatus: null,
                errorMsg: '',
                statusOptions: [
                    { value: 'Subscribed', label: 'On for all loans', help: 'You get this notification on every loan.' },
                    { value: 'Unsubscribed', label: 'Off for all loans', help: 'You never get this notification.' },
                    { value: 'Partially Unsubscribed', label: 'Off for specific loans', help: 'On, except for the loans you list.' }
                ],
                errorTitle: '',
                loading: false,
                notificationMsg: '',
                partiallyUnsubscribed: false,
                showDuplicateLoanError: false,
                showErrorDialog: false,
                showInvalidLoanError: false,
                subscribed: false,
                successNotification: false,
                uneditedUnsubscribedLoans: [],
                unsubscribed: false,
                unsubscribedLoanNumber: null,
                unsubscribedLoans: [],
                uneditedSubscriptions: [],
                uneditedSubscriptionStatus: null
            }
        },
        computed: {
            dialogTitle() {
                const channels = { email: 'Email', external: 'External', SMS: 'SMS', Push: 'Push' };
                const channel = channels[this.notificationType] || '';

                return `Manage ${channel} subscription: ${this.notification?.title || ''}`;
            },
            dialogSubtitle() {
                const channels = { email: 'Email', external: 'External', SMS: 'SMS', Push: 'Push' };

                return [channels[this.notificationType], this.notification?.title].filter(Boolean).join(' · ');
            },
            saveChangesBtnDisabled() {
                let uneditedLoans;
                let editedLoans;

                if (this.uneditedSubscriptionStatus === 'Partially Unsubscribed' && this.editedSubscriptionStatus === 'Partially Unsubscribed') {
                    let uneditedLoans = cloneDeep(this.uneditedUnsubscribedLoans);
                    let editedLoans = cloneDeep(this.unsubscribedLoans);

                    uneditedLoans.sort((a, b) => a.loanNumber.localeCompare(b.loanNumber));
                    editedLoans.sort((a, b) => a.loanNumber.localeCompare(b.loanNumber));

                    if (this.unsubscribedLoans?.length === 0 || isEqual(uneditedLoans, editedLoans)) {
                        return true;
                    } else {
                        return false;
                    }
                }

                return  (this.editedSubscriptionStatus === 'Partially Unsubscribed' && this.unsubscribedLoans?.length === 0)                       
                        || this.uneditedSubscriptionStatus === this.editedSubscriptionStatus;
            }
        },
        watch: {
            showDialog(val) {
                if (val) {
                    this.uneditedSubscriptions = cloneDeep(this.notificationSubscriptions);

                    this.unsubscribedLoans = this.notificationSubscriptions?.reduce((prev, cur) => {
                        if (cur.loanNumber) {
                            prev.push({
                                borrowerFirst: cur.borrowerFirst,
                                borrowerLast: cur.borrowerLast,
                                loanId: cur.loanId,
                                loanNumber: cur.loanNumber
                            });
                        }

                        return prev;
                    }, []);

                    this.uneditedUnsubscribedLoans = cloneDeep(this.unsubscribedLoans);

                    if (this.notification) {
                        this.uneditedSubscriptionStatus = this.currentSubscriptionStatus;
                        this.editedSubscriptionStatus = this.currentSubscriptionStatus;
                        this.setCurrentSubscriptionStatus(this.uneditedSubscriptionStatus);
                    }
                } else {
                    this.resetDefaults();
                }
            }
        },
        methods: {
            async deleteExternalNotificationOptOuts(subIds) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.deleteExternalNotificationOptOuts(subIds, tokenResponse.accessToken);
                } catch (error) {
                    let errorTitle = 'Failed to Update Subscriptions';

                    let errorMsg;

                    if(error.response?.data?.data) {
                        errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        errorMsg = '';
                    }

                    this.onRequestError(errorTitle, errorMsg);
                }
            },
            async deleteNotificationOptOuts(subIds) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.deleteNotificationOptOuts(subIds, tokenResponse.accessToken);
                } catch (error) {
                    let errorTitle = 'Failed to Update Subscriptions';

                    let errorMsg;

                    if(error.response?.data?.data) {
                        errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        errorMsg = '';
                    }

                    this.onRequestError(errorTitle, errorMsg);
                }
            },
            async deletePushOptIns(subIds) {
                const tokenResponse = await auth.acquireTokenResponse();

               try {
                    await subscriptionHelpers.deletePushOptIns(subIds, tokenResponse.accessToken);
               } catch (error) {
                    let errorTitle = 'Failed to Update Subscriptions';

                    let errorMsg;

                    if(error.response?.data?.data) {
                        errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        errorMsg = '';
                    }

                    this.onRequestError(errorTitle, errorMsg);
               }
            },
            async deleteSMSOptIns(subIds) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.deleteSMSOptIns(subIds, tokenResponse.accessToken);
                } catch (error) {
                    let errorTitle = 'Failed to Update Subscriptions';

                    let errorMsg;

                    if(error.response?.data?.data) {
                        errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        errorMsg = '';
                    }

                    this.onRequestError(errorTitle, errorMsg);
                }
            },
            async getLoanDisplayData(loanNumber) {
                this.loading = true;
                const tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetLoanDisplayData(loanNumber, tokenResponse.accessToken) 
                    .then(res => {
                        this.loading = false;

                        if (res?.data?.data) {
                            this.unsubscribedLoans.push(res.data.data);
                            this.unsubscribedLoanNumber = null;
                        } else {
                            this.showInvalidLoanError = true;
                        }
                    }).catch(error => {
                        let errorTitle = 'Failed to Find Loan by Loan Number';

                        let errorMsg;

                        if(error.response?.data?.data) {
                            errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            errorMsg = '';
                        }

                        this.onRequestError(errorTitle, errorMsg);
                    });
            },
            async onAddLoanNumber() {
                let loan = this.unsubscribedLoans.find(loan => loan.loanNumber === this.unsubscribedLoanNumber);

                if (!loan) {
                    this.showDuplicateLoanError = false;
                    await this.getLoanDisplayData(this.unsubscribedLoanNumber);
                } else {
                    this.showDuplicateLoanError = true;
                }
            },
            onCancelClicked() {
                this.$emit('dialogclosed');
            },
            onCloseDialog() {
                this.$emit('dialogclosed');
            },
            onCloseNotification(flag) {
                this[flag] = false;
            },
            onRemoveLoan(loan) {
                let removedIndex = this.unsubscribedLoans.findIndex(l => l.loanId === loan.loanId);

                if (removedIndex > -1) {
                    this.unsubscribedLoans.splice(removedIndex, 1);
                }
            },
            onRequestError(errorTitle, errorMsg) {
                this.loading = false;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.showErrorDialog = true;
            },
            onSaveClicked() {
                this.loading = true;

                if (this.notificationType === 'email') {
                    this.saveEmailSubscriptions();
                }

                if (this.notificationType === 'SMS' || this.notificationType === 'Push') {
                    this.saveSMSPushSubscriptions();
                }

                if (this.notificationType === 'external') {
                    this.saveExternalSubscriptions();
                }
            },
            resetDefaults() {
                this.editedSubscriptions = [];
                this.uneditedSubscriptions = [];
                this.uneditedSubscriptionStatus = null;
                this.editedSubscriptionStatus = null;
                this.unsubscribedLoans = [];
                this.uneditedUnsubscribedLoans = [];
                this.unsubscribedLoanNumber = null;
                this.showDuplicateLoanError = false;
                this.showInvalidLoanError = false;
            },
            resetErrors() {
                this.showDuplicateLoanError = false;
                this.showInvalidLoanError = false;
            },
            async saveEmailSubscriptions() {
                if (this.editedSubscriptionStatus === 'Subscribed') {
                    let subIds = this.notificationSubscriptions.map(sub => sub.guid);

                    if (this.unsubscribedFromAllEmailsItem) {
                        subIds.push(this.unsubscribedFromAllEmailsItem.guid);
                    }

                    if (subIds.length > 0) {
                        await this.deleteNotificationOptOuts(subIds);
                    }

                    if (this.unsubscribedFromAllEmails) {
                        let optOuts = this.allNotifications.filter(noti => noti.id !== this.notification.id).map(noti => {
                            return {
                                guid: null,
                                azureId: this.azureId,
                                templateId: noti.id,
                                loanId: null,
                                type: 'email'
                            }
                        });

                        await this.upsertNotificationOptOuts(optOuts);
                    }
                }

                if (this.editedSubscriptionStatus === 'Unsubscribed') {
                    let subIds = this.notificationSubscriptions.map(sub => sub.guid);

                    if (subIds.length > 0) {
                        await this.deleteNotificationOptOuts(subIds);
                    }

                    let optOuts = [
                        {
                            guid: null,
                            azureId: this.azureId,
                            templateId: this.notification.id,
                            loanId: null,
                            type: 'email'
                        }
                    ]

                    await this.upsertNotificationOptOuts(optOuts);
                }

                if (this.editedSubscriptionStatus === 'Partially Unsubscribed') {
                    let subIds = this.notificationSubscriptions.map(sub => sub.guid);

                    if (this.unsubscribedFromAllEmailsItem) {
                        subIds.push(this.unsubscribedFromAllEmailsItem.guid);
                    }

                    if (subIds.length > 0) {
                        await this.deleteNotificationOptOuts(subIds);
                    }

                    let optOuts = [];

                    if (this.unsubscribedFromAllEmails) {
                        optOuts = this.allNotifications.filter(noti => noti.id !== this.notification.id).map(noti => {
                            return {
                                guid: null,
                                azureId: this.azureId,
                                templateId: noti.id,
                                loanId: null,
                                type: 'email'
                            }
                        });
                    }

                    optOuts = [...optOuts, ...this.unsubscribedLoans.map(loan => {
                        return {
                            guid: null,
                            azureId: this.azureId,
                            templateId: this.notification.id,
                            loanId: loan.loanId,
                            type: 'email'
                        }
                    })];

                    await this.upsertNotificationOptOuts(optOuts);
                }

                this.$emit('subscriptionsupdated');

                this.showNotification('Subscriptions updated successfully');
                this.loading = false;
            },
            async saveExternalSubscriptions() {
                if (this.editedSubscriptionStatus === 'Subscribed') {
                    let subIds = this.notificationSubscriptions.map(sub => sub.id);

                    if (subIds.length > 0) {
                        await this.deleteExternalNotificationOptOuts(subIds);
                    }
                }

                if (this.editedSubscriptionStatus === 'Unsubscribed') {
                    let subIds = this.notificationSubscriptions.map(sub => sub.id);

                    if (subIds.length > 0) {
                        await this.deleteExternalNotificationOptOuts(subIds);
                    }

                    let optOuts = [
                        {
                            id: null,
                            azureId: this.azureId,
                            templateId: this.notification.id,
                            loanId: null
                        }
                    ]

                    await this.upsertExternalNotificationOptOuts(optOuts);
                }

                if (this.editedSubscriptionStatus === 'Partially Unsubscribed') {
                    let subIds = this.notificationSubscriptions.map(sub => sub.id);

                    if (subIds.length > 0) {
                        await this.deleteExternalNotificationOptOuts(subIds);
                    }

                    let optOuts = this.unsubscribedLoans.map(loan => {
                        return {
                            id: null,
                            azureId: this.azureId,
                            templateId: this.notification.id,
                            loanId: loan.loanId
                        }
                    });

                    await this.upsertExternalNotificationOptOuts(optOuts);
                }

                this.loading = false;
                this.$emit('subscriptionsupdated');

                this.showNotification('Subscriptions updated successfully');
            },
            async saveSMSPushSubscriptions() {
                let subscribedToAll = this.notificationType === 'SMS' ? this.subscribedToAllSms : this.subscribedToAllPush;
                let subscribedToAllItem = this.notificationType === 'SMS' ? this.subscribedToAllSmsItem : this.subscribedToAllPushItem;

                if (this.editedSubscriptionStatus === 'Subscribed') {
                    let subAll = this.notificationSubscriptions.find(sub => !sub.optOut);

                    let subIds = this.notificationSubscriptions.filter(sub => sub.optOut).map(sub => sub.guid);

                    if (subIds.length) {
                        if (this.notificationType === 'SMS') {
                            await this.deleteSMSOptIns(subIds);
                        } else {
                            await this.deletePushOptIns(subIds);
                        }
                    }

                    if (!subAll) {
                        let request = [
                            {
                                guid: null,
                                azureId: this.azureId,
                                loanId: null,
                                templateId: this.notification.id,
                                type: this.notificationType,
                                optOut: false
                            }
                        ]

                        if (this.notificationType === 'SMS') {
                            await this.upsertSMSOptIns(request);
                        } else {
                            await this.upsertPushOptIns(request);
                        }

                    }
                }

                if (this.editedSubscriptionStatus === 'Unsubscribed') {
                    let subIds = this.notificationSubscriptions.map(sub => sub.guid);

                    if (subscribedToAllItem) {
                        subIds.push(subscribedToAllItem.guid);
                    }

                    if (subIds.length > 0) {
                        if (this.notificationType === 'SMS') {
                            await this.deleteSMSOptIns(subIds);
                        } else {
                            await this.deletePushOptIns(subIds);
                        }
                    }

                    if (subscribedToAll) {
                        let optIns = this.allNotifications.filter(noti => noti.id !== this.notification.id).map(noti => {
                            return {
                                guid: null,
                                azureId: this.azureId,
                                templateId: noti.id,
                                loanId: null,
                                type: this.notificationType,
                                optOut: false
                            }
                        });

                        if (this.notificationType === 'SMS') {
                            await this.upsertSMSOptIns(optIns);
                        } else {
                            await this.upsertPushOptIns(optIns);
                        }
                    }
                }

                if (this.editedSubscriptionStatus === 'Partially Unsubscribed') {
                    let subAll = this.notificationSubscriptions.find(sub => !sub.optOut);

                    let subIds = this.notificationSubscriptions.filter(sub => sub.optOut).map(sub => sub.guid);

                    if (subscribedToAllItem) {
                        subIds.push(subscribedToAllItem.guid);
                    }

                    if (subIds.length > 0) {
                        if (this.notificationType === 'SMS') {
                            await this.deleteSMSOptIns(subIds);
                        } else {
                            await this.deletePushOptIns(subIds);
                        }
                    }

                    let optIns = [];

                    if (subscribedToAll) {
                        optIns = this.allNotifications.filter(noti => noti.id !== this.notification.id).map(noti => {
                            return {
                                guid: null,
                                azureId: this.azureId,
                                templateId: noti.id,
                                loanId: null,
                                type: this.notificationType,
                                optOut: false
                            }
                        });
                    }

                    if (!subAll) {
                        optIns.push({
                            guid: null,
                            azureId: this.azureId,
                            loanId: null,
                            templateId: this.notification.id,
                            type: this.notificationType,
                            optOut: false
                        });
                    }

                    optIns = [...optIns, ...this.unsubscribedLoans.map(loan => {
                        return {
                            guid: null,
                            azureId: this.azureId,
                            templateId: this.notification.id,
                            loanId: loan.loanId,
                            type: this.notificationType,
                            optOut: true
                        }
                    })];

                    if (this.notificationType === 'SMS') {
                        await this.upsertSMSOptIns(optIns);
                    } else {
                        await this.upsertPushOptIns(optIns);
                    }
                }

                this.loading = false;
                this.$emit('subscriptionsupdated');

                this.showNotification('Subscriptions updated successfully');
            },
            setCurrentSubscriptionStatus(status) {
                this.subscribed = status === 'Subscribed';
                this.unsubscribed = status === 'Unsubscribed';
                this.partiallyUnsubscribed = status === 'Partially Unsubscribed';
            },
            showNotification(notificationMsg) {
                this.notificationMsg = notificationMsg;

                this.successNotification = true;

                setTimeout(() => {
                    this.successNotification = false;
                }, 4000);
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if(!this.showErrorDialog) {
                    this.errorTitle = '';
                }
            },
            updateStatus(status) {
                this.editedSubscriptionStatus = status;
                this.setCurrentSubscriptionStatus(this.editedSubscriptionStatus);
            },
            async upsertExternalNotificationOptOuts(request) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.upsertExternalNotificationOptOuts(request, tokenResponse.accessToken);
                } catch (error) {
                    let errorTitle = 'Failed to Update Subscriptions';

                    let errorMsg;

                    if(error.response?.data?.data) {
                        errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        errorMsg = '';
                    }

                    this.onRequestError(errorTitle, errorMsg);
                }
            },
            async upsertNotificationOptOuts(request) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.upsertNotificationOptOuts(request, tokenResponse.accessToken);
                } catch (error) {
                    let errorTitle = 'Failed to Update Subscriptions';

                    let errorMsg;

                    if(error.response?.data?.data) {
                        errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        errorMsg = '';
                    }

                    this.onRequestError(errorTitle, errorMsg);
                }
            },
            async upsertPushOptIns(request) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.upsertPushOptIns(request, tokenResponse.accessToken);
                } catch (error) {
                    let errorTitle = 'Failed to Update Subscriptions';

                    let errorMsg;

                    if(error.response?.data?.data) {
                        errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        errorMsg = '';
                    }

                    this.onRequestError(errorTitle, errorMsg);
                }
            },
            async upsertSMSOptIns(request) {
                const tokenResponse = await auth.acquireTokenResponse();

                try {
                    await subscriptionHelpers.upsertSMSOptIns(request, tokenResponse.accessToken)
                } catch (error) {
                    let errorTitle = 'Failed to Update Subscriptions';

                    let errorMsg;

                    if(error.response?.data?.data) {
                        errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        errorMsg = '';
                    }

                    this.onRequestError(errorTitle, errorMsg);
                }
            }
        },
        components: {
            'k-dialog': Dialog,
            'k-action-bar': DialogActionsBar,
            'k-button': Button,
            'k-loader': Loader,
            'k-notification': Notification,
            'k-notification-group': NotificationGroup,
            'k-fade': Fade,
            'k-input': Input
        }
    }

</script>

<style>
    @import '/src/assets/css/editSubscriptionDialog.css';
</style>